import { useState, useEffect, useCallback } from 'react';
import { 
  Search, 
  Trash2,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  Mail,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { DeleteUserModal } from '../modals/DeleteUserModal';
import { InviteUserModal } from '../modals/InviteUserModal';
import api from '@/api';
import { Loader2, AlertCircle } from 'lucide-react';
import { notifications } from '@/utils/notifications';
import { useEffectiveViewport } from '@/hooks/useEffectiveViewport';
import { useAccountRole } from '@/hooks/useAccountRole';
import { SettingsReadOnlyBanner } from '@/components/settings/SettingsReadOnlyBanner';
import { formatApiErrorMessage } from '@/utils/apiErrorMessage';

// --- Types ---

type AccountMemberRole = 'admin' | 'user';

type UserMember = {
  uid: string;
  name: string;
  email: string;
  status: boolean;
  lastActive?: string;
  role?: AccountMemberRole;
  isOwner?: boolean;
  isAdmin?: boolean;
};

// --- Sub-components ---

const userInitials = (name: string) =>
  (name || '??')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2);

const UserAvatar = ({ name }: { name: string }) => {
  const initials = userInitials(name);
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary/15 bg-primary/10 text-2xs font-semibold text-primary">
      {initials}
    </div>
  );
};

const StatusBadge = ({ user }: { user: UserMember }) => {
  const { t } = useTranslation('settings');

  return (
    <div className="flex items-center gap-1.5 text-xs font-medium">
      <span
        className={clsx('h-1.5 w-1.5 shrink-0 rounded-full', user.status ? 'bg-green-500' : 'bg-amber-400')}
      />
      <span className={user.status ? 'text-foreground/80' : 'text-amber-600 dark:text-amber-400'}>
        {user.status ? t('team.statusActive') : t('team.statusInvited')}
      </span>
    </div>
  );
};

const roleFieldClass =
  'h-8 max-w-[7.5rem] rounded-md border border-border bg-background px-2 text-xs font-medium leading-none text-foreground';

const RoleReadonlyField = ({ label }: { label: string }) => (
  <span
    className={clsx(
      roleFieldClass,
      'inline-flex items-center bg-muted/25 text-muted-foreground'
    )}
  >
    {label}
  </span>
);

const RoleBadge = ({ user }: { user: UserMember }) => {
  const { t } = useTranslation('settings');
  const label = user.isOwner
    ? t('team.roleOwner')
    : user.role === 'admin' || user.isAdmin
      ? t('team.roleAdmin')
      : t('team.roleUser');
  return <RoleReadonlyField label={label} />;
};

const RoleControl = ({
  user,
  canManage,
  isWorkspaceOwner,
  onRoleChange,
}: {
  user: UserMember;
  canManage: boolean;
  isWorkspaceOwner: boolean;
  onRoleChange: (user: UserMember, role: AccountMemberRole) => void;
}) => {
  const { t } = useTranslation('settings');
  if (user.isOwner || !isWorkspaceOwner || !canManage) {
    return <RoleBadge user={user} />;
  }
  return (
    <select
      value={user.role === 'admin' || user.isAdmin ? 'admin' : 'user'}
      onChange={(e) => onRoleChange(user, e.target.value as AccountMemberRole)}
      className={roleFieldClass}
      aria-label={t('team.roleColumn')}
    >
      <option value="user">{t('team.roleUser')}</option>
      <option value="admin">{t('team.roleAdmin')}</option>
    </select>
  );
};

const UserActions = ({
  user,
  onDelete,
  onResend,
  resendingUid,
  canManage,
}: {
  user: UserMember;
  onDelete: (user: UserMember) => void;
  onResend: (user: UserMember) => void;
  resendingUid: string | null;
  canManage: boolean;
}) => {
  const { t } = useTranslation('settings');
  if (!canManage || user.isOwner) return null;
  const isResending = resendingUid === user.uid;
  const showResend = !user.status;

  return (
    <div className="flex shrink-0 items-center gap-1">
      {showResend && (
        <button
          type="button"
          onClick={() => onResend(user)}
          disabled={isResending}
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={t('team.resendInviteAria', { name: user.name })}
        >
          {isResending ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
        </button>
      )}
      <button
        type="button"
        onClick={() => onDelete(user)}
        className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
        aria-label={t('team.removeUserAria', { name: user.name })}
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};

/** Stacked row for small screens — avoids wide tables and stray scrollbars */
const UserMobileRow = ({
  user,
  onDelete,
  onResend,
  resendingUid,
  canManage,
  onRoleChange,
  isWorkspaceOwner,
}: {
  user: UserMember;
  onDelete: (user: UserMember) => void;
  onResend: (user: UserMember) => void;
  resendingUid: string | null;
  canManage: boolean;
  onRoleChange: (user: UserMember, role: AccountMemberRole) => void;
  isWorkspaceOwner: boolean;
}) => (
  <UserMobileRowInner
    user={user}
    onDelete={onDelete}
    onResend={onResend}
    resendingUid={resendingUid}
    canManage={canManage}
    onRoleChange={onRoleChange}
    isWorkspaceOwner={isWorkspaceOwner}
  />
);

const UserMobileRowInner = ({
  user,
  onDelete,
  onResend,
  resendingUid,
  canManage,
  onRoleChange,
  isWorkspaceOwner,
}: {
  user: UserMember;
  onDelete: (user: UserMember) => void;
  onResend: (user: UserMember) => void;
  resendingUid: string | null;
  canManage: boolean;
  onRoleChange: (user: UserMember, role: AccountMemberRole) => void;
  isWorkspaceOwner: boolean;
}) => {
  return (
    <div className="px-4 py-3">
      <div className="flex gap-3">
        <UserAvatar name={user.name} />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">{user.name}</p>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">{user.email}</p>
            </div>
            <UserActions
              user={user}
              onDelete={onDelete}
              onResend={onResend}
              resendingUid={resendingUid}
              canManage={canManage}
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <RoleControl
              user={user}
              canManage={canManage}
              isWorkspaceOwner={isWorkspaceOwner}
              onRoleChange={onRoleChange}
            />
            <StatusBadge user={user} />
            <span className="text-xs text-muted-foreground">{user.lastActive || '—'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const UserRow = ({
  user,
  onDelete,
  onResend,
  resendingUid,
  canManage,
  onRoleChange,
  isWorkspaceOwner,
}: {
  user: UserMember;
  onDelete: (user: UserMember) => void;
  onResend: (user: UserMember) => void;
  resendingUid: string | null;
  canManage: boolean;
  onRoleChange: (user: UserMember, role: AccountMemberRole) => void;
  isWorkspaceOwner: boolean;
}) => {
  return (
  <tr className="group transition-colors hover:bg-muted/20">
    <td className="px-4 py-2.5 md:px-5">
      <div className="flex items-center gap-2.5">
        <UserAvatar name={user.name} />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{user.name}</p>
          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
        </div>
      </div>
    </td>
    <td className="px-4 py-2.5 md:px-5">
      <RoleControl
        user={user}
        canManage={canManage}
        isWorkspaceOwner={isWorkspaceOwner}
        onRoleChange={onRoleChange}
      />
    </td>
    <td className="px-4 py-2.5 md:px-5">
      <StatusBadge user={user} />
    </td>
    <td className="px-4 py-2.5 md:px-5">
      <span className="text-xs text-muted-foreground">{user.lastActive || '—'}</span>
    </td>
    <td className="px-3 py-2.5 text-right md:px-4">
      <div className="inline-flex justify-end">
        <UserActions
          user={user}
          onDelete={onDelete}
          onResend={onResend}
          resendingUid={resendingUid}
          canManage={canManage}
        />
      </div>
    </td>
  </tr>
  );
};

// --- Main Section ---

export const UsersSettings = () => {
  const { t } = useTranslation('settings');
  const { t: tToast } = useTranslation('notifications');
  const { t: tCommon } = useTranslation('common');
  const { canManageAccount, isWorkspaceOwner } = useAccountRole();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; user: UserMember | null }>({
    isOpen: false,
    user: null
  });
  const [users, setUsers] = useState<UserMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [resendingUid, setResendingUid] = useState<string | null>(null);
  const { isWide: isUsersTableWide } = useEffectiveViewport();

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<{ status: string; users: UserMember[] }>('/users');
      if (response.data.status === 'success') {
        setUsers(response.data.users);
      }
    } catch (err: unknown) {
      console.error('Fetch users error:', err);
      const message = err instanceof Error ? err.message : t('team.fetchFailed');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleInvite = async (payload: {
    name: string;
    email: string;
    role?: AccountMemberRole;
  }): Promise<boolean> => {
    try {
      const response = await api.post<{
        status: 'success' | 'error';
        message?: string;
        uid?: string;
      }>('/users/add', {
        name: payload.name,
        email: payload.email,
        role: payload.role ?? 'user',
      });
      if (response.data.status === 'success') {
        notifications.success(
          response.data.message || tToast('users.invitedSuccess')
        );
        if (response.data.uid) {
          const invitedUid = response.data.uid;
          setUsers((prev) => {
            if (prev.some((u) => u.uid === invitedUid)) return prev;
            const newUser: UserMember = {
              uid: invitedUid,
              name: payload.name,
              email: payload.email,
              status: false,
              role: payload.role ?? 'user',
            };
            return [...prev, newUser];
          });
        }
        return true;
      }
      notifications.error(response.data.message || t('team.inviteFailed'));
      return false;
    } catch (err: unknown) {
      console.error('Invite user error:', err);
      const message = err instanceof Error ? err.message : t('team.inviteFailed');
      notifications.error(message);
      return false;
    }
  };

  const handleDeleteUser = (user: UserMember) => {
    setDeleteModal({ isOpen: true, user });
  };

  const handleResendInvite = async (user: UserMember) => {
    if (resendingUid) return;
    try {
      setResendingUid(user.uid);
      const response = await api.post<{ status: 'success' | 'error'; message?: string }>(
        '/users/resendinvite',
        { uid: user.uid }
      );
      if (response.data.status === 'success') {
        notifications.success(response.data.message || tToast('users.inviteResentSuccess'));
      } else {
        notifications.error(response.data.message || t('team.resendInviteFailed'));
      }
    } catch (err: unknown) {
      console.error('Resend invite error:', err);
      const message = err instanceof Error ? err.message : t('team.resendInviteFailed');
      notifications.error(message);
    } finally {
      setResendingUid(null);
    }
  };

  const onDeleteSuccess = (uid: string) => {
    setUsers(prev => prev.filter(u => u.uid !== uid));
  };

  const handleRoleChange = async (user: UserMember, role: AccountMemberRole) => {
    const previousRole: AccountMemberRole =
      user.role === 'admin' || user.isAdmin ? 'admin' : 'user';
    if (role === previousRole) return;

    try {
      const response = await api.patch<{ status: string; message?: string; code?: string }>(
        '/users/role',
        {
          uid: user.uid,
          role,
        }
      );
      if (response.data.status === 'success') {
        setUsers((prev) =>
          prev.map((u) => (u.uid === user.uid ? { ...u, role, isAdmin: role === 'admin' } : u))
        );
        notifications.success(t('team.roleUpdated'));
      } else {
        notifications.error(
          formatApiErrorMessage(
            { response: { data: response.data } },
            tCommon,
            response.data.message || t('team.roleUpdateFailed')
          )
        );
      }
    } catch (err: unknown) {
      notifications.error(formatApiErrorMessage(err, tCommon, t('team.roleUpdateFailed')));
    }
  };

  const filteredUsers = users.filter(u =>
    (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="animate-in space-y-5 fade-in slide-in-from-bottom-2 duration-300">
      {!canManageAccount ? <SettingsReadOnlyBanner /> : null}
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-foreground">{t('team.title')}</h2>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t('team.subtitle')}</p>
        </div>
        {canManageAccount ? (
        <button
          type="button"
          onClick={() => setIsInviteModalOpen(true)}
          className="inline-flex min-h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 sm:min-h-0"
        >
          <UserPlus size={16} />
          {t('team.inviteUser')}
        </button>
        ) : null}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          type="search"
          enterKeyHint="search"
          placeholder={t('team.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/10"
        />
      </div>

      {/* Members: stacked list (mobile) + table (md+) */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {loading ? (
          <div className="flex flex-col items-center gap-3 py-16 sm:py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary sm:h-9 sm:w-9" />
            <p className="text-sm font-medium text-muted-foreground">{t('team.loadingMembers')}</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 px-4 py-16 text-center text-red-400 sm:py-20">
            <AlertCircle className="h-8 w-8 sm:h-9 sm:w-9" />
            <p className="text-sm font-bold">{error}</p>
            <button
              type="button"
              onClick={fetchUsers}
              className="mt-1 font-black text-primary hover:underline"
            >
              {t('team.tryAgain')}
            </button>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="px-4 py-16 text-center text-muted-foreground sm:py-20">
            <p className="text-sm font-medium">{t('team.noUsersFound')}</p>
          </div>
        ) : (
          <>
            {!isUsersTableWide ? (
              <div>
                <div className="border-b border-border-muted bg-muted/30 px-4 py-2">
                  <p className="text-xs font-medium text-muted-foreground">{t('team.members')}</p>
                </div>
                <div className="divide-y divide-border-muted">
                  {filteredUsers.map((user) => (
                    <UserMobileRow
                      key={user.uid}
                      user={user}
                      onDelete={handleDeleteUser}
                      onResend={handleResendInvite}
                      resendingUid={resendingUid}
                      canManage={canManageAccount}
                      onRoleChange={handleRoleChange}
                      isWorkspaceOwner={isWorkspaceOwner}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[36rem] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-border-muted bg-muted/30">
                      <th className="table-th px-4 py-2 md:px-5">{t('team.userColumn')}</th>
                      <th className="table-th px-4 py-2 md:px-5">{t('team.roleColumn')}</th>
                      <th className="table-th px-4 py-2 md:px-5">{t('team.statusColumn')}</th>
                      <th className="table-th px-4 py-2 md:px-5">{t('team.lastActiveColumn')}</th>
                      <th className="w-16 px-3 py-2 md:px-4" aria-hidden />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-muted">
                    {filteredUsers.map((user) => (
                      <UserRow
                        key={user.uid}
                        user={user}
                        onDelete={handleDeleteUser}
                        onResend={handleResendInvite}
                        resendingUid={resendingUid}
                        canManage={canManageAccount}
                        onRoleChange={handleRoleChange}
                        isWorkspaceOwner={isWorkspaceOwner}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {!loading && !error && (
          <div className="flex flex-col gap-2 border-t border-border-muted bg-muted/20 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <span className="text-xs text-muted-foreground">
              {t('team.showingUsers', { count: filteredUsers.length })}
            </span>
            <div className="flex justify-center gap-1.5 sm:justify-end">
              <button
                type="button"
                disabled
                className="flex h-8 w-8 items-center justify-center rounded-md border border-border-muted text-muted-foreground/40"
                aria-label={t('team.previousPage')}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted"
                aria-label={t('team.nextPage')}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      <InviteUserModal 
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleInvite}
        allowAdminInvite={isWorkspaceOwner}
      />

      <DeleteUserModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ ...deleteModal, isOpen: false })}
        uid={deleteModal.user?.uid || ''}
        userName={deleteModal.user?.name || ''}
        onSuccess={onDeleteSuccess}
      />
    </div>
  );
};
